import { CallMedium } from '@zoopla-eng/zds/icons';
import { LinkWithIcon } from '@zoopla-eng/zds/link';
import { fontGroups } from '@zoopla-eng/zds/themes';
import { Spinner } from '@zoopla/assets';
import { useDynamicNumber } from '@zoopla/dynamic-phone-number';
import { queries } from '@zoopla-eng/zds/themes';
import { useBreakpointListener } from '@zoopla/dom-util';
import React, { useEffect, useRef, useState } from 'react';
import {
    contactLinkStyle,
    contactWrapperStyle,
    loaderStyle
} from './contact.styles.css';

export type ContactPhoneProps = {
    phoneNumber?: string | null;
    branchId?: number | null;
    listingId: string;
    onRevealPhoneNumberClick?: () => void;
    onPhoneNumberClick?: () => void;
};

const ContactPhone: React.FC<ContactPhoneProps> = ({
    phoneNumber,
    branchId,
    listingId,
    onRevealPhoneNumberClick,
    onPhoneNumberClick
}) => {
    const [shownPhoneNumber, setShownPhoneNumber] = useState<
        string | undefined | null
    >(undefined);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const callLinkRef = useRef<HTMLAnchorElement>(null);
    const isMobile = useBreakpointListener(queries.M[100]);

    const { getDynamicNumber, dynamicPhone, loading, error } =
        useDynamicNumber();

    if (error && !shownPhoneNumber) {
        setShownPhoneNumber(phoneNumber);
    }

    if (dynamicPhone && !shownPhoneNumber) {
        setShownPhoneNumber(dynamicPhone);
    }

    const isPhoneNumberVisible = () => shownPhoneNumber || !branchId;

    const getCallText = () => {
        if (shownPhoneNumber && !isMobile) {
            return shownPhoneNumber;
        } else if (loading) {
            return <img className={loaderStyle} src={Spinner} alt="loading" />;
        } else {
            return 'Call';
        }
    };

    const getPhoneNumberLink = () => {
        return shownPhoneNumber ? `tel:${shownPhoneNumber}` : '';
    };

    const handleIovoxClick = () => {
        onRevealPhoneNumberClick?.();
        timer.current = setTimeout(() => {
            setShownPhoneNumber((prevNumber) =>
                prevNumber ? prevNumber : phoneNumber
            );
        }, 2000);
        getDynamicNumber(String(branchId), listingId);
    };

    useEffect(() => {
        return () => {
            if (timer.current) clearInterval(timer.current);
        };
    }, []);

    useEffect(() => {
        if (isMobile && isPhoneNumberVisible()) {
            callLinkRef.current?.click();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shownPhoneNumber]);

    try {
        return (
            <div className={contactWrapperStyle}>
                <div className={`${contactLinkStyle} ${fontGroups.ladle}`}>
                    {isPhoneNumberVisible() ? (
                        <LinkWithIcon
                            href={getPhoneNumberLink()}
                            onClick={onPhoneNumberClick}
                            aria-label="Call agent"
                            iconPrefix={<CallMedium />}
                            ref={callLinkRef}
                        >
                            {getCallText()}
                        </LinkWithIcon>
                    ) : (
                        <LinkWithIcon
                            onClick={handleIovoxClick}
                            aria-label="Reveal agents telephone number"
                            iconPrefix={<CallMedium />}
                        >
                            {getCallText()}
                        </LinkWithIcon>
                    )}
                </div>
            </div>
        );
    } catch (e) {
        return null;
    }
};

export { ContactPhone };
